import { ReviewAsset, ReviewAssetType, ReviewerRole, ReviewPrivacy, ReviewType } from './types'
import { ReviewTemplate } from 'types'
import { Value } from 'components/ShareSheet/RecipientSelector/Types'
import { ReviewFrequency, Weekday } from './ReviewersSelection/constants'

export enum EventId {
  SELECT_TYPE = 'SELECT_TYPE',
  CONTINUE = 'CONTINUE',
  CLEAR = 'CLEAR',
  BACK = 'BACK',
  SELECT_PRIVACY = 'SELECT_PRIVACY',
  SET_TITLE = 'SET_TITLE',
  SELECT_REVIEWERS = 'SELECT_REVIEWERS',
  SELECT_APPROVERS = 'SELECT_APPROVERS',
  SELECT_VIEWERS = 'SELECT_VIEWERS',
  SET_EXTERNAL_LINK = 'SET_EXTERNAL_LINK',
  SET_EXTERNAL_FILE_ICON = 'SET_EXTERNAL_FILE_ICON',
  SET_ASSET = 'SET_ASSET',
  SET_ASSET_TITLE = 'SET_ASSET_TITLE',
  SET_MESSAGE = 'SET_MESSAGE',
  SET_END_DATE = 'SET_END_DATE',
  INVITE_GUESTS = 'INVITE_GUESTS',
  INVITE_TO_WORKSPACE = 'INVITE_TO_WORKSPACE',
  SELECT_SUGGESTERS = 'SELECT_SUGGESTERS',
  SELECT_REVIEW_TEMPLATE = 'SELECT_REVIEW_TEMPLATE',
  ADD_REVIEWER_ROLE = 'ADD_REVIEWER_ROLE',
  VIEW_ALL_TEMPLATES = 'VIEW_ALL_TEMPLATES',
  OPEN_TEMPLATE_FORM = 'OPEN_TEMPLATE_FORM',
  TEMPLATE_SAVED = 'TEMPLATE_SAVED',
  SET_INTERVAL = 'SET_INTERVAL',
}

export interface Context {
  title: string
  reviewType: ReviewType | null
  reviewPrivacy: ReviewPrivacy
  reviewers: Value[]
  approvers: Value[]
  viewers: Value[]
  message: string
  htmlMessage?: string
  endDate: string
  suggesters: Value[]
  asset: ReviewAsset | null
  reviewTemplate: ReviewTemplate | null
  reviewRoles: ReviewerRole[]
  interval: string
  assetType: ReviewAssetType | null
  layerId?: string
  weekdays: Weekday[]
  frequency?: ReviewFrequency
  // Whether the step to select a type of review should be disabled. This is
  // useful when navigating directly to the reviewers selection from outside the
  // wizard.
  disableTypeSelection: boolean
}

export enum StateId {
  ALL_TEMPLATES = 'allTemplates',
  FILE_SELECTION = 'fileSelection',
  TYPE_SELECTION = 'typeSelection',
  REVIEWERS_SELECTION = 'reviewersSelection',
  INVITE_EMAILS = 'inviteEmails',
  SUGGESTERS_SELECTION = 'suggestersSelection',
  TEMPLATE_FORM = 'templateForm',
  TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG = 'typeSelectionFetchingReviewTypeConfig',
  REVIEWERS_SELECTION_FETCHING_REVIEW_TYPE_CONFIG = 'reviewersSelectionFetchingReviewTypeConfig',
}

export type State =
  | {
      value: StateId.ALL_TEMPLATES
      context: Context
    }
  | {
      value: StateId.FILE_SELECTION
      context: Context
    }
  | {
      value: StateId.TYPE_SELECTION
      context: Context
    }
  | {
      value: StateId.TYPE_SELECTION_FETCHING_REVIEW_TYPE_CONFIG
      context: Context
    }
  | {
      value: StateId.REVIEWERS_SELECTION
      context: Context
    }
  | {
      value: StateId.REVIEWERS_SELECTION_FETCHING_REVIEW_TYPE_CONFIG
      context: Context
    }
  | {
      value: StateId.INVITE_EMAILS
      context: Context
    }
  | {
      value: StateId.SUGGESTERS_SELECTION
      context: Context
    }
  | {
      value: StateId.TEMPLATE_FORM
      context: Context
    }

export type ViewAllTemplatesEvent = {
  type: EventId.VIEW_ALL_TEMPLATES
}

export type SelectTypeEvent = {
  type: EventId.SELECT_TYPE
  reviewType: ReviewType
}

export type SelectPrivacyEvent = {
  type: EventId.SELECT_PRIVACY
  reviewPrivacy: ReviewPrivacy
}

export type SetTitleEvent = {
  type: EventId.SET_TITLE
  title: string
}

export type SetAssetTitleEvent = {
  type: EventId.SET_ASSET_TITLE
  assetTitle: string
}

export type SelectReviewersEvent = {
  type: EventId.SELECT_REVIEWERS
  reviewers: Value[]
}

export type SelectApproversEvent = {
  type: EventId.SELECT_APPROVERS
  approvers: Value[]
}

export type SelectViewersEvent = {
  type: EventId.SELECT_VIEWERS
  viewers: Value[]
}

export type SelectSuggestersEvent = {
  type: EventId.SELECT_SUGGESTERS
  suggesters: Value[]
}

export type SetMessageEvent = {
  type: EventId.SET_MESSAGE
  message: string
  htmlMessage?: string
}

export type SetExternalLinkEvent = {
  type: EventId.SET_EXTERNAL_LINK
  externalLink: string
}

export type SetExternalFileIconEvent = {
  type: EventId.SET_EXTERNAL_FILE_ICON
  externalFileIcon: string
}

export type SetAssetEvent = {
  type: EventId.SET_ASSET
  asset: ReviewAsset
  assetType: ReviewAssetType
}

export type SetEndDateEvent = {
  type: EventId.SET_END_DATE
  endDate: string
}

export type InviteGuestsEvent = {
  type: EventId.INVITE_GUESTS
}

export type InviteToWorkspaceEvent = {
  type: EventId.INVITE_TO_WORKSPACE
}

export type ContinueEvent = {
  type: EventId.CONTINUE
  newEmails?: string[]
  fetchConfig?: boolean
}

export type ClearEvent = {
  type: EventId.CLEAR
}

export type BackEvent = {
  type: EventId.BACK
}

export type SelectReviewTemplate = {
  type: EventId.SELECT_REVIEW_TEMPLATE
  reviewTemplate: ReviewTemplate
}

export type OpenTemplateForm = {
  type: EventId.OPEN_TEMPLATE_FORM
}

export type TemplateSaved = {
  type: EventId.TEMPLATE_SAVED
  reviewTemplate: ReviewTemplate
}

type AddReviewerRole = {
  type: EventId.ADD_REVIEWER_ROLE
  role: ReviewerRole
}

type SetInterval = {
  type: EventId.SET_INTERVAL
  interval: string
  frequency: ReviewFrequency
  weekdays: Weekday[]
}

export type ReviewWizardEvent =
  | SelectTypeEvent
  | SelectPrivacyEvent
  | ClearEvent
  | SetAssetTitleEvent
  | ContinueEvent
  | BackEvent
  | SetTitleEvent
  | SelectReviewersEvent
  | SelectApproversEvent
  | SelectViewersEvent
  | SetMessageEvent
  | SetExternalLinkEvent
  | SetExternalFileIconEvent
  | SetAssetEvent
  | SetEndDateEvent
  | InviteGuestsEvent
  | InviteToWorkspaceEvent
  | SelectSuggestersEvent
  | SelectReviewTemplate
  | AddReviewerRole
  | ViewAllTemplatesEvent
  | OpenTemplateForm
  | TemplateSaved
  | SetInterval
